import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Subscription, Observable } from 'rxjs'
import { finalize } from 'rxjs/operators'
import { AccountService } from '../../services/account.service'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { RoleLoadingService, RoleItem as BaseRoleItem, AccessLevelInfo } from 'src/app/services/role-loading.service'
import { handleArpioError } from 'src/utils/handle-error'

// Extended interface for user-related operations
export interface RoleItem extends BaseRoleItem {
  source?: 'direct' | 'group' | 'sso' // Track if role is directly assigned, inherited from group, or from SSO
  groupNames?: string[] // Track which groups provide this role (for inherited roles)
}

// Re-export base interface for compatibility
export { AccessLevelInfo }

export interface GroupItem {
  id: string
  name: string
  roleCount: number
  selected: boolean
  accountSubjects?: string[] // Array of account subject IDs that belong to this group
  roles?: string[] // Array of role IDs assigned to this group
  source?: 'sso' // Track if group is from SSO provider
}

export abstract class BaseUserModal {
  // Common properties for user operations
  selectedRoles: RoleItem[] = []
  selectedGroups: GroupItem[] = []
  availableRoles: RoleItem[] = []
  availableGroups: GroupItem[] = []

  // Initial state for change tracking (used by edit modals)
  protected initialRoleIds: string[] = []
  protected initialGroupIds: string[] = []
  protected initialStateCaptured = false

  // Form and loading states
  userForm: FormGroup
  loadingRoles = false
  saving = false
  saveSubscription: Subscription = null

  constructor(
    protected formBuilder: FormBuilder,
    protected accountService: AccountService,
    protected apiService: ApiService,
    protected uiService: UiService,
    protected accessLevelService: AccessLevelService,
    protected roleLoadingService: RoleLoadingService
  ) {
    this.initializeForm()
  }

  // Helper method to identify default account roles
  isDefaultAccountRole(role: RoleItem): boolean {
    return this.accessLevelService.isDefaultAccountRole(role)
  }

  // Abstract methods that must be implemented by child components
  abstract getSaveButtonText(): string
  abstract performSave(userData: any): Observable<any>
  abstract onSaveSuccess(result: any): void
  abstract getFormData(): any

  // Common form initialization (can be overridden)
  protected initializeForm() {
    this.userForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
    })
  }

  // Common roles and groups loading
  protected async loadRolesAndGroups() {
    this.loadingRoles = true
    try {
      await Promise.all([this.loadAvailableRoles(), this.loadAvailableGroups()])
    } catch (error) {
      console.error('Failed to load roles and groups:', error)
    } finally {
      this.loadingRoles = false
    }
  }

  private async loadAvailableRoles(): Promise<void> {
    const allRoles = await this.roleLoadingService.loadRoles()

    // Filter to only system roles if rbacFull is not enabled
    if (!this.accountService.rbacFullEnabled) {
      this.availableRoles = allRoles.filter((role) => role.isSystemRole)
    } else {
      this.availableRoles = allRoles
    }
  }

  private loadAvailableGroups(): Promise<void> {
    return new Promise((resolve) => {
      this.apiService.listAccountSubjectGroups(this.accountService.accountId).subscribe(
        (groupDtos) => {
          this.availableGroups = groupDtos.map((groupDto) => ({
            id: groupDto.accountSubjectGroupId || '',
            name: groupDto.name || '',
            roleCount: groupDto.roles?.length || 0,
            selected: false,
            accountSubjects: groupDto.accountSubjects || [],
            roles: groupDto.roles || [],
          }))
          resolve()
        },
        (error) => {
          console.error('Failed to load groups:', error)
          this.availableGroups = []
          resolve()
        }
      )
    })
  }

  // Common validation logic
  get isFormValid(): boolean {
    const hasValidForm = this.userForm.valid
    const hasSelectedRoles = this.selectedRoles.length > 0
    const hasSelectedGroups = this.selectedGroups.length > 0
    const hasInheritedRoles = this.availableRoles.some((role) => role.source === 'group')
    const hasSsoRoles = this.availableRoles.some((role) => role.source === 'sso')

    return hasValidForm && (hasSelectedRoles || hasSelectedGroups || hasInheritedRoles || hasSsoRoles)
  }

  // Change tracking for edit modals
  protected captureInitialState(): void {
    this.initialRoleIds = this.selectedRoles.map((r) => r.id).sort()
    this.initialGroupIds = this.selectedGroups.map((g) => g.id).sort()
    this.initialStateCaptured = true
  }

  get hasChanges(): boolean {
    // If initial state was never captured (e.g., new user modal), always consider it as having changes
    if (!this.initialStateCaptured) {
      return true
    }

    const currentRoleIds = this.selectedRoles.map((r) => r.id).sort()
    const currentGroupIds = this.selectedGroups.map((g) => g.id).sort()

    const rolesChanged =
      currentRoleIds.length !== this.initialRoleIds.length ||
      currentRoleIds.some((id, idx) => id !== this.initialRoleIds[idx])

    const groupsChanged =
      currentGroupIds.length !== this.initialGroupIds.length ||
      currentGroupIds.some((id, idx) => id !== this.initialGroupIds[idx])

    return rolesChanged || groupsChanged
  }

  get canSave(): boolean {
    return this.isFormValid && this.hasChanges
  }

  // Common save logic
  onSave() {
    if (!this.isFormValid) return

    this.saving = true

    const userData = this.getFormData()

    this.saveSubscription = this.performSave(userData)
      .pipe(
        finalize(() => {
          this.saving = false
          this.saveSubscription = null
        })
      )
      .subscribe(
        (result) => {
          this.onSaveSuccess(result)
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  // Common reset functionality
  protected resetSelections() {
    this.selectedRoles = []
    this.selectedGroups = []
    this.initialRoleIds = []
    this.initialGroupIds = []
    this.initialStateCaptured = false
    this.availableRoles.forEach((role) => {
      role.selected = false
      role.source = undefined
      role.groupNames = undefined
    })
    this.availableGroups.forEach((group) => (group.selected = false))
  }

  // Common cleanup
  protected cleanup() {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe()
      this.saveSubscription = null
    }
  }

  // Helper methods for role/group selection
  getSelectedRoleIds(): string[] {
    return this.selectedRoles.map((role) => role.id)
  }

  getSelectedGroupIds(): string[] {
    return this.selectedGroups.map((group) => group.id)
  }

  // Common form error handling
  get nameErrors() {
    const control = this.userForm.get('name')
    if (control?.errors && control?.touched) {
      if (control.errors['required']) {
        return 'Name is required'
      }
    }
    return null
  }

  get emailErrors() {
    const control = this.userForm.get('email')
    if (control?.errors && control?.touched) {
      if (control.errors['required']) {
        return 'Email is required'
      }
      if (control.errors['email']) {
        return 'Please enter a valid email address'
      }
    }
    return null
  }

  // Custom checkbox methods for roles
  areAllRolesSelected(): boolean {
    const assignableRoles = this.availableRoles.filter((role) => role.source !== 'group')
    return this.selectedRoles.length > 0 && this.selectedRoles.length === assignableRoles.length
  }

  areSomeRolesSelected(): boolean {
    const assignableRoles = this.availableRoles.filter((role) => role.source !== 'group')
    return this.selectedRoles.length > 0 && this.selectedRoles.length < assignableRoles.length
  }

  isRoleSelected(role: RoleItem): boolean {
    return this.selectedRoles.some((r) => r.id === role.id)
  }

  toggleAllRoles(event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    // Only toggle directly assignable roles (not group-inherited roles)
    const assignableRoles = this.availableRoles.filter((role) => role.source !== 'group')

    if (isChecked) {
      this.selectedRoles = [...assignableRoles]
      assignableRoles.forEach((role) => (role.selected = true))
    } else {
      this.selectedRoles = []
      assignableRoles.forEach((role) => (role.selected = false))
    }
  }

  toggleRole(role: RoleItem, event: Event): void {
    // Don't allow toggling roles inherited from groups
    if (role.source === 'group') {
      return
    }

    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      if (!this.isRoleSelected(role)) {
        this.selectedRoles = [...this.selectedRoles, role]
        role.selected = true
      }
    } else {
      this.selectedRoles = this.selectedRoles.filter((r) => r.id !== role.id)
      role.selected = false
    }
  }

  // Custom checkbox methods for groups
  areAllGroupsSelected(): boolean {
    return this.selectedGroups.length > 0 && this.selectedGroups.length === this.availableGroups.length
  }

  areSomeGroupsSelected(): boolean {
    return this.selectedGroups.length > 0 && this.selectedGroups.length < this.availableGroups.length
  }

  isGroupSelected(group: GroupItem): boolean {
    return this.selectedGroups.some((g) => g.id === group.id)
  }

  toggleAllGroups(event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      this.selectedGroups = [...this.availableGroups]
    } else {
      this.selectedGroups = []
    }
  }

  toggleGroup(group: GroupItem, event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      if (!this.isGroupSelected(group)) {
        this.selectedGroups = [...this.selectedGroups, group]
      }
    } else {
      this.selectedGroups = this.selectedGroups.filter((g) => g.id !== group.id)
    }
  }

  // Generate tooltip content for access level badges
  getAccessLevelTooltip(accessLevelInfo: AccessLevelInfo): string {
    if (!accessLevelInfo.applications || accessLevelInfo.applications.length === 0) {
      return 'All Applications'
    }

    const badgeHtml = accessLevelInfo.applications.map((name) => `<span class="tooltip-badge">${name}</span>`).join('')
    return `<div class="tooltip-badges-container">${badgeHtml}</div>`
  }
}
